import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	NgZone,
	ChangeDetectorRef,
	Renderer2,
	inject
} from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { select, Store } from "@ngrx/store";
import { Subject, Subscription } from "rxjs";
import { Location } from "@angular/common";
import { startWith, take, takeUntil } from "rxjs/operators";
import { formatISO } from "date-fns";

import { CONTRACT_TYPES } from "../../../../../shared/constants/contract-types";
import { DateAdapter } from "@angular/material/core";
import { Employee } from "../../../../../shared/models/employee.interface";
import { PayGroup } from "../../../../../shared/models/pay-groups";
import { getCustomerEntityGroupState } from "../../../../../store";
import { AppState } from "../../../../../store/models/state.model";
import { PayElectiveCommonService } from "../../../services/pay-elective-common.service";
import { PermissionsService } from "../../../../../shared/services/permissions/permissions.service";
import { BreadcrumbService } from "@shared/components/breadcrumbs/services/breadcrumb.service";
import { MatLegacySelectChange } from "@angular/material/legacy-select";
import { numberValidator } from "@shared/validators/number.validator";
import { QaLocatorService } from "@shared/utils/qa-locator.service";

@Component({
	selector: "app-pay-elective-employee",
	templateUrl: "./pay-elective-employee.component.html",
	styleUrls: ["./pay-elective-employee.component.scss"]
})
export class PayElectiveEmployeeComponent implements OnInit, OnDestroy {
	@Output() employeeCreated = new EventEmitter<string>();
	@Output() employeeNameAltered = new EventEmitter<string>();

	// Handle payGroup asynchronously via a setter; patch values when it arrives
	private _payGroup?: PayGroup;
	@Input() set payGroup(val: PayGroup | undefined) {
		if (!val) return;
		this._payGroup = val;

		this.form.patchValue({
			enityReference: val.legalEntity?.data?.externalId ?? "",
			payGroupExternalId: val.externalId ?? "",
			payGroupId: val.id ?? ""
		});

		this.formCurrency = val.data?.currency ?? "GBP";
	}

	_employee?: Employee;
	@Input() set employee(val: Employee | undefined) {
		if (!val) return;
		this._employee = val;
		this.isEdit = true;

		const patch: Record<string, string | Date | number | null> = {
			externalId: val.externalId ?? "",
			firstName: val.data?.firstName ?? "",
			lastName: val.data?.lastName ?? "",
			startDate: this.dateAdapter.deserialize(val.data?.startDate),
			endDate: this.dateAdapter.deserialize(val.data?.endDate),
			contractType: val.data?.contractType ?? "SB",
			email: val.data?.email ?? ""
		};

		if (this.form.get("netSalary")) {
			patch.netSalary = val.data?.netSalary ?? null;
		}
		if (this.form.get("hourlyRate")) {
			patch.hourlyRate = val.data?.hourlyRate ?? null;
		}

		this.form.patchValue(patch);

		this.firstNameInput = val.data?.firstName ?? "";
		this.lastNameInput = val.data?.lastName ?? "";

		if (!this.form.get("id")) this.form.addControl("id", this.formBuilder.control(val.id));
		else this.form.get("id")!.setValue(val.id);

		if (!this.form.get("version")) this.form.addControl("version", this.formBuilder.control(val.version));
		else this.form.get("version")!.setValue(val.version);
	}

	customerId = "";
	legalEntityId = "";

	isEdit = false;

	entityPayGroupSelection: Subscription = new Subscription();
	destroy$ = new Subject<void>();

	firstNameInput = "";
	lastNameInput = "";

	contractTypes = CONTRACT_TYPES;

	formCurrency = "GBP";
	cpNowIntegrationEnabled = false;
	paygroupCPNowToggleOn = false;

	isDisabled = false;

	/** IMPORT DECLARATION */
	private renderer = inject(Renderer2);
	private formBuilder = inject(FormBuilder);
	private payElectiveCommonService = inject(PayElectiveCommonService);
	private store = inject(Store<AppState>);
	private readonly _location = inject(Location);
	private permissionsService = inject(PermissionsService);
	private breadCrumbService = inject(BreadcrumbService);
	private dateAdapter = inject(DateAdapter<Date>);
	private zone = inject(NgZone);
	private cdr = inject(ChangeDetectorRef);
	private qaLocatorService = inject(QaLocatorService);

	form: FormGroup = this.formBuilder.group({
		externalId: [{ value: "", disabled: this.isDisabled }, [Validators.required, Validators.minLength(1)]],
		firstName: [
			{ value: "", disabled: this.isDisabled },
			[
				Validators.required,
				Validators.minLength(1),
				Validators.maxLength(100),
				Validators.pattern("^[a-zA-Z][a-zA-Z -]*$")
			]
		],
		lastName: [
			{ value: "", disabled: this.isDisabled },
			[
				Validators.required,
				Validators.minLength(1),
				Validators.maxLength(100),
				Validators.pattern("^[a-zA-Z][a-zA-Z -]*$")
			]
		],
		enityReference: [{ value: "", disabled: true }],
		payGroupExternalId: [{ value: "", disabled: true }],
		payGroupId: [{ value: "", disabled: this.isDisabled }]
	});

	ngOnInit(): void {
		this.store.pipe(takeUntil(this.destroy$), select(getCustomerEntityGroupState)).subscribe(state => {
			this.customerId = state.customerId;
			this.legalEntityId = state.legalEntityId;
		});

		if (this._employee) {
			this.isEdit = true;
			this.permissionsService
				.canEditEmployee()
				.pipe(takeUntil(this.destroy$))
				.subscribe(enable => {
					this.isDisabled = !enable;
					this.toggleDisabledState();
				});
		} else {
			this.permissionsService
				.canCreateEmployee()
				.pipe(takeUntil(this.destroy$))
				.subscribe(enable => {
					this.isDisabled = !enable;
					this.toggleDisabledState();
				});
		}

		if (this._payGroup?.data.cloudPayNow) {
			this.paygroupCPNowToggleOn = true;
		}

		this.payElectiveCommonService
			.isCPNowIntegrationEnabled(this._payGroup?.id ?? "")
			.pipe(take(1))
			.subscribe(hasFeature => {
				this.cpNowIntegrationEnabled = hasFeature;

				if (this.cpNowIntegrationEnabled && !this.form.get("startDate")) {
					this.form.addControl("startDate", this.formBuilder.control<Date | null>(null, Validators.required));
					this.form.addControl("endDate", this.formBuilder.control<Date | null>(null));

					if (this.paygroupCPNowToggleOn) {
						this.form.addControl("email", this.formBuilder.control("", [Validators.email]));

						// Add Contract Type
						this.form.addControl("contractType", this.formBuilder.control("SB", [Validators.required]));

						//

						// Add or remove net salary and hourly rate based on contract type
						this.form
							.get("contractType")
							?.valueChanges.pipe(
								startWith(this._employee?.data?.contractType ?? "SB"),
								takeUntil(this.destroy$)
							)
							.subscribe((type: "SB" | "HR" | null) => {
								if (!type) {
									if (this.form.get("netSalary")) this.form.removeControl("netSalary");
									if (this.form.get("hourlyRate")) this.form.removeControl("hourlyRate");
								} else if (type === "SB") {
									if (this.form.get("netSalary")) this.form.removeControl("hourlyRate");
									this.form.addControl("netSalary", this.formBuilder.control("", [numberValidator]));
								} else if (type === "HR") {
									this.form.removeControl("netSalary");
									this.form.addControl("hourlyRate", this.formBuilder.control("", [numberValidator]));
								}

								// If any cross-field validators depend on these values:
								this.form.updateValueAndValidity({ emitEvent: false });
							});
					}

					// Patch the values if in edit mode
					if (this.isEdit && this._employee) {
						const val = this._employee;
						const patch: Record<string, string | Date | number | null> = {
							externalId: val.externalId ?? "",
							firstName: val.data?.firstName ?? "",
							lastName: val.data?.lastName ?? "",
							startDate: this.dateAdapter.deserialize(val.data?.startDate),
							endDate: this.dateAdapter.deserialize(val.data?.endDate)
						};

						if (this.form.get("email")) {
							patch.email = val.data?.email ?? "";
						}

						if (this.form.get("contractType")) {
							patch.contractType = val.data?.contractType ?? "SB";
						}

						if (this.form.get("netSalary")) {
							patch.netSalary = val.data?.netSalary ?? null;
						}
						if (this.form.get("hourlyRate")) {
							patch.hourlyRate = val.data?.hourlyRate ?? null;
						}

						this.form.patchValue(patch);
					}
					this.form.setValidators(this.checkDates);
					this.form.updateValueAndValidity({ emitEvent: false });
				}
			});

		this.form
			.get("firstName")
			?.valueChanges.pipe(takeUntil(this.destroy$))
			.subscribe(input => {
				this.firstNameInput = input ?? "";
				this.employeeNameAltered.emit(`${this.firstNameInput} ${this.lastNameInput}`);
			});

		this.form
			.get("lastName")
			?.valueChanges.pipe(takeUntil(this.destroy$))
			.subscribe(input => {
				this.lastNameInput = input ?? "";
				this.employeeNameAltered.emit(`${this.firstNameInput} ${this.lastNameInput}`);
			});

		this.initSubscriptions();
	}

	private toggleDisabledState() {
		// Central place to enable/disable controls based on permission
		// Validators are ignored while disabled, so you can keep them defined.
		const toToggle = ["externalId", "firstName", "lastName", "payGroupId"];
		toToggle.forEach(key => {
			const ctrl = this.form.get(key);
			if (!ctrl) return;
			this.isDisabled ? ctrl.disable({ emitEvent: false }) : ctrl.enable({ emitEvent: false });
		});
	}

	cancel(): void {
		this._location.historyGo(-1);
	}

	onChangePayGroup(): void {
		this.payElectiveCommonService.openDialog(
			this.customerId,
			this.legalEntityId,
			this._payGroup!,
			this._employee?.id!
		);
	}

	storePayElective(): void {
		//check if form contains startDate and Enddate then update them to formatISO(new Date(formValue.effectiveFrom), { representation: "date" }) to remove time
		const formValue = this.form.getRawValue();
		if (formValue.startDate) {
			formValue.startDate = formatISO(new Date(formValue.startDate), { representation: "date" });
		}
		if (formValue.endDate) {
			formValue.endDate = formatISO(new Date(formValue.endDate), { representation: "date" });
		}

		this.payElectiveCommonService.storePayElective(formValue, this.isEdit);
		this.employeeCreated.emit();
	}

	initSubscriptions(): void {
		this.entityPayGroupSelection = this.payElectiveCommonService.entityPayGroupSelection$
			.pipe(takeUntil(this.destroy$))
			.subscribe(res => {
				if (res) {
					this.form.patchValue({
						enityReference: res.legalEntity.data.externalId,
						payGroupExternalId: res.externalId,
						payGroupId: res.id
					});
				}
			});

		// IMPORTANT: do NOT call this.ngOnInit() here again
		this.breadCrumbService.paygroupUpdated$.pipe(takeUntil(this.destroy$)).subscribe({
			next: res => {
				// If you need to refresh from a server-side change, do it explicitly here,
				// or trigger a service to re-fetch and patch, but avoid re-entering ngOnInit.
				// Example:
				// if (res) this.reFetchAndPatchPayGroup();
			}
		});
	}

	checkDates: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
		const group = control as FormGroup;
		const startCtrl = group.get("startDate");
		const endCtrl = group.get("endDate");

		if (!startCtrl || !endCtrl) return null;

		const startRaw = startCtrl.value;
		const endRaw = endCtrl.value;
		if (!startRaw || !endRaw) return null;

		const start = new Date(startRaw);
		const end = new Date(endRaw);
		if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

		return end < start ? { dateOrder: true } : null;
	};

	onContractTypeChange(event: MatLegacySelectChange) {
		const selected = event.value;
		this.form.get("contractType")!.setValue(selected, { emitEvent: true });
	}

	/**
	 * Initialize QA locators
	 * @param datePickerName: date picker name/used
	 */
	openDatePicker(datePickerName: string): void {
		setTimeout(() => {
			this.datePickerViewChange("month", datePickerName);
		});
	}

	/**
	 * Call function to render QA locators for date-picker
	 * @param view: date picker current view
	 * @param datePickerName: date picker name/used
	 */
	datePickerViewChange(view: string, datePickerName: string): void {
		this.qaLocatorService.datePickerViewChange(view, datePickerName, this.renderer);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
